import JSZip from 'jszip';
import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
import { embedBodyFonts, embedStandardFallbackFonts } from './pdfFonts';
import type { PdfExportQuality } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import type { PageDims } from './pdfPageDims';

export interface PptxPdfOptions {
  quality: PdfExportQuality;
  pageDims: PageDims;
  oneSlidePerPage: boolean;
  preserveAspectRatio: boolean;
  includeSpeakerNotes: boolean;
}

function drawFooter(page: ReturnType<PDFDocument['getPage']>, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pw = page.getWidth();
  page.drawText(label, {
    x: pw - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

function applyFooters(doc: PDFDocument, font: PDFFont, margin: number): void {
  doc.getPages().forEach((p, i, arr) => drawFooter(p, i, arr.length, font, margin));
}

function extractAText(xml: string): string {
  const parts: string[] = [];
  const re = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    const chunk = m[1]?.replace(/\s+/g, ' ').trim();
    if (chunk) parts.push(chunk);
  }
  return parts.join('\n');
}

function slideNumber(path: string): number {
  const match = /slide(\d+)\.xml$/i.exec(path);
  return match ? parseInt(match[1]!, 10) : 0;
}

export async function convertPptxBufferToPdf(buffer: ArrayBuffer, opts: PptxPdfOptions): Promise<Uint8Array> {
  const zip = await JSZip.loadAsync(buffer);
  const slidePaths = Object.keys(zip.files)
    .filter((k) => /^ppt\/slides\/slide\d+\.xml$/i.test(k) && !k.includes('_rels'))
    .sort((a, b) => slideNumber(a) - slideNumber(b));

  if (!slidePaths.length) throw new Error('No slides found in presentation');

  const layout = layoutForQuality(opts.quality);
  const doc = await PDFDocument.create();
  let fonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }

  const { widthPt, heightPt } = opts.pageDims;
  const bottom = layout.margin + layout.footerReserved;

  let page = doc.addPage([widthPt, heightPt]);
  let y = heightPt - layout.margin;

  const ensureSpace = (need: number) => {
    if (y - need < bottom) {
      page = doc.addPage([widthPt, heightPt]);
      y = heightPt - layout.margin;
    }
  };

  const drawParagraph = (text: string, size: number, font: typeof fonts.regular, color: ReturnType<typeof rgb>, lineMult: number) => {
    const lineH = size * lineMult;
    const approxLines = Math.ceil(text.length / Math.max(48, Math.floor((widthPt - 2 * layout.margin) / (size * 0.52))));
    const need = lineH * Math.min(approxLines, 40) + 10;
    ensureSpace(need);
    page.drawText(text, {
      x: layout.margin,
      y,
      size,
      font,
      color,
      maxWidth: widthPt - 2 * layout.margin,
      lineHeight: lineH,
    });
    y -= lineH * Math.min(approxLines, 40) + 12;
  };

  for (let i = 0; i < slidePaths.length; i += 1) {
    if (opts.oneSlidePerPage && i > 0) {
      page = doc.addPage([widthPt, heightPt]);
      y = heightPt - layout.margin;
    }

    const path = slidePaths[i]!;
    const xml = await zip.file(path)?.async('string');
    const slideText = xml ? extractAText(xml) : '';

    let notesText = '';
    if (opts.includeSpeakerNotes) {
      const notesPath = `ppt/notesSlides/notesSlide${slideNumber(path)}.xml`;
      const nx = await zip.file(notesPath)?.async('string');
      if (nx) notesText = extractAText(nx);
    }

    ensureSpace(layout.sectionTitleSize * 2);
    page.drawText(`Slide ${i + 1}`, {
      x: layout.margin,
      y,
      size: layout.sectionTitleSize + 1,
      font: fonts.bold,
      color: rgb(0.12, 0.13, 0.18),
    });
    y -= layout.sectionTitleSize * 1.55;

    drawParagraph(slideText.trim() || '(No extractable text on this slide)', layout.bodySize, fonts.regular, rgb(0.2, 0.21, 0.24), layout.lineHeightMult);

    if (notesText.trim()) {
      ensureSpace(layout.sectionTitleSize * 2);
      page.drawText('Speaker notes', {
        x: layout.margin,
        y,
        size: layout.sectionTitleSize,
        font: fonts.bold,
        color: rgb(0.28, 0.3, 0.34),
      });
      y -= layout.sectionTitleSize * 1.45;
      drawParagraph(notesText.trim(), layout.metaSize, fonts.regular, rgb(0.28, 0.29, 0.32), 1.38);
    }

    if (!opts.oneSlidePerPage) {
      y -= 18;
    }
  }

  void opts.preserveAspectRatio;

  applyFooters(doc, fonts.regular, layout.margin);
  return doc.save();
}
